import React from "react";
import {
  Button,
  Col,
  Divider,
  Input,
  Row,
  Select,
  Space,
  Table,
  Tag,
} from "antd";
import {
  PlusOutlined,
  ExclamationCircleOutlined,
  UnorderedListOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { startPostActividad } from "../../../context/actions/seguimientoActions";
import { startSetIncidencia2 } from "../../../context/actions/incidenciasActions";
import {
  ActividadesSeguimientoOptions,
  motivoIncidenciaArriboOpciones,
} from "../../../constants/cons";
import { URL_ACTIVIDADES_SEGUIMIENTO, URL_ASISTENCIA_INCIDENCIA } from "../../../constants/url";
import { dateFromString } from "../../../utils/dateFromString";
import { useFetchList, useFormPost } from "./hooks";

const tagColorByName = { Llamada: "blue", Whatsapp: "green", Correo: "cyan", SMS: "orange" };

const columnsActividades = [
  {
    title: "Fecha",
    dataIndex: "fecha",
    key: "fecha",
    width: 140,
    render: (f) => dateFromString(f).f1,
    sorter: (a, b) => new Date(a.fecha) - new Date(b.fecha),
  },
  {
    title: "Usuario",
    dataIndex: "usuarioNombre",
    key: "usuarioNombre",
    width: 140,
  },
  {
    title: "Tipo",
    dataIndex: "tipoActividadSeguimiento",
    key: "tipoActividadSeguimiento",
    width: 110,
    render: (text) => (
      <Tag color={tagColorByName[text] || "default"}>{text}</Tag>
    ),
  },
  {
    title: "Comentarios",
    dataIndex: "comentarios",
    key: "comentarios",
  },
];

const columnsIncidencias = [
  {
    title: "Fecha",
    dataIndex: "fechaIncidencia",
    key: "fechaIncidencia",
    width: 140,
    render: (f) => dateFromString(f).f1,
    sorter: (a, b) => new Date(a.fechaIncidencia) - new Date(b.fechaIncidencia),
  },
  {
    title: "Usuario",
    dataIndex: "usuarioIncidenciaNombre",
    key: "usuarioIncidenciaNombre",
    width: 140,
  },
  {
    title: "Momento",
    dataIndex: "momentoIncidencia",
    key: "momentoIncidencia",
    width: 110,
    render: (v) => v === 1 ? <Tag color="volcano">Asignación</Tag> : <Tag color="geekblue">Arribo</Tag>,
  },
  {
    title: "Motivo",
    dataIndex: "motivoIncidencia",
    key: "motivoIncidencia",
    render: (text) => <Tag color="red">{text}</Tag>,
  },
];

export const DetalleServicio = ({ servicio }) => {
  const asistenciaID = servicio.asistenciaID;

  const { listado: actividades, loading: loadingAct, refetch: refetchActividades } =
    useFetchList(`${URL_ACTIVIDADES_SEGUIMIENTO}/GetActividadesByAsistenciaID/${asistenciaID}`);
  const { listado: incidencias, loading: loadingInc, refetch: refetchIncidencias } =
    useFetchList(`${URL_ASISTENCIA_INCIDENCIA}/GetIncidenciasByAsistenciaID/${asistenciaID}`);

  const refreshAfterActividad = () => {
    refetchActividades();
  };

  return (
    <div style={{ padding: "10px 4px" }}>
      <Row gutter={16}>
        <Col span={12}>
          <FormActividad asistenciaID={asistenciaID} onSuccess={refreshAfterActividad} />
          <Divider orientation="left"><UnorderedListOutlined /> Actividades</Divider>
          <Table
            columns={columnsActividades}
            dataSource={actividades}
            rowKey={(r) => `${r.fecha}-${r.usuarioNombre}`}
            loading={loadingAct}
            size="small"
            pagination={false}
            scroll={{ y: 300 }}
            locale={{ emptyText: "Sin actividades" }}
          />
        </Col>
        <Col span={12}>
          <FormIncidencia asistenciaID={asistenciaID} onSuccess={refetchIncidencias} />
          <Divider orientation="left"><WarningOutlined /> Incidencias</Divider>
          <Table
            columns={columnsIncidencias}
            dataSource={incidencias}
            rowKey={(r) => `${r.fechaIncidencia}-${r.usuarioIncidenciaNombre}`}
            loading={loadingInc}
            size="small"
            pagination={false}
            scroll={{ y: 300 }}
            locale={{ emptyText: "Sin incidencias" }}
          />
        </Col>
      </Row>
    </div>
  );
};

const FormActividad = ({ asistenciaID, onSuccess }) => {
  const { fields, setField, submit, sending } = useFormPost(
    startPostActividad,
    (f) => ({ asistenciaID, ...f }),
    onSuccess
  );

  const disabled = !fields.tipoActividadSeguimientoID || !fields.comentarios;

  return (
    <Space size="small">
      <Select
        value={fields.tipoActividadSeguimientoID}
        onChange={(v) => setField("tipoActividadSeguimientoID", v)}
        style={{ width: 130 }}
        placeholder="Actividad"
        options={ActividadesSeguimientoOptions}
        disabled={sending}
      />
      <Input
        onChange={(e) => setField("comentarios", e.target.value)}
        value={fields.comentarios || ""}
        placeholder="Comentario"
        style={{ width: 180 }}
        disabled={sending}
      />
      <Button disabled={disabled || sending} onClick={submit} type="primary" icon={<PlusOutlined />}>
        Actividad
      </Button>
    </Space>
  );
};

const FormIncidencia = ({ asistenciaID, onSuccess }) => {
  const { fields, setField, submit, sending } = useFormPost(
    startSetIncidencia2,
    (f) => ({ asistenciaID, motivoIncidencia: f.motivoIncidencia, momentoIncidencia: 2 }),
    onSuccess
  );

  const disabled = !fields.motivoIncidencia;

  return (
    <Space size="small">
      <Select
        value={fields.motivoIncidencia}
        onChange={(v) => setField("motivoIncidencia", v)}
        style={{ width: 200 }}
        placeholder="Incidencia"
        options={motivoIncidenciaArriboOpciones}
        disabled={sending}
      />
      <Button
        disabled={disabled || sending}
        onClick={submit}
        type="primary"
        danger
        icon={<ExclamationCircleOutlined />}
      >
        Incidencia
      </Button>
    </Space>
  );
};
